/**
 * Web-only sessionStorage mirror for route query params that need to
 * survive a hard reload.
 * @remarks Why this exists (SE-4722). Expo Router on web normalizes
 * the URL during route hydration on a hard reload — it computes the
 * route's canonical path from navigation state and writes it back via
 * `history.replaceState`. Query params that the surrounding navigator
 * does not "know about" can disappear in that step before any React
 * code observes them. A reload of `/squad?tab=contracts` lands at
 * `/squad`, so a screen that holds tab state in the URL alone falls
 * back to its default tab even though the user never asked to leave.
 *
 * The original SE-4722 fix worked around this by snapshotting
 * `window.location.search` at module-load time (one constant per
 * persisted param, web-only, and — as SE-6816 measured — losing a race
 * against the canonicalization it was meant to beat). This helper
 * replaces that pattern: every user-initiated change to a
 * persisted param is mirrored into sessionStorage, so the next mount
 * — even after the URL has been stripped — can read the most-recent
 * intended value back. Adding a new persisted param then needs zero
 * extra plumbing; it just goes through `persistRouteParam` /
 * `readPersistedRouteParam`.
 *
 * sessionStorage rather than localStorage is intentional. The
 * semantic we want is "remember where the user was while they're in
 * this browsing session" — closing the tab clears the slate, which
 * matches the expectation that a brand-new tab starts at the screen's
 * default. localStorage would persist across sessions and surprise
 * users with sticky state that no longer reflects intent.
 *
 * On native there is no `window.sessionStorage` and no URL
 * canonicalization to fight, so these calls are no-ops; React
 * Navigation persists the active route stack on its own.
 * @module utils/persistedRouteParams
 */

const STORAGE_PREFIX = "expo-router-param";

/**
 * Whether the runtime exposes a usable sessionStorage. False on
 * native (no `window`) and during server rendering.
 * @returns `true` when `window.sessionStorage` can be read/written
 */
function isAvailable(): boolean {
  if (typeof window === "undefined") return false;
  try {
    return typeof window.sessionStorage !== "undefined";
  } catch {
    return false;
  }
}

/**
 * The query string the document was ORIGINALLY fetched with, read back
 * from the navigation timing entry.
 *
 * Only consulted when `window.location.search` is already empty, which
 * is precisely the case module-load timing cannot cover. `location`
 * reflects every `history.replaceState` the app has performed since
 * boot, and Expo Router's canonicalization is such a call — so once it
 * has run, the arrival query string is gone from `location` forever.
 * `PerformanceNavigationTiming.name` is the URL the *document* was
 * fetched with and is never rewritten by `replaceState`, so it still
 * holds the params the user actually arrived with.
 *
 * This is what makes {@link INITIAL_URL_PARAMS} independent of WHEN
 * this module is first evaluated. Metro runs a module's factory on the
 * first `require`, and nothing requires this one until the screen that
 * imports it renders — which happens after the router has mounted and
 * canonicalized. The two were in a race that the snapshot lost as often
 * as it won; `/squad?tab=contracts` opened cold therefore landed on
 * Planning, and so did every other deep link routed through here.
 * @returns The arrival query string (`"?a=b"`), or `""` when the
 * navigation entry is unavailable or unparseable
 */
function readArrivalSearch(): string {
  try {
    const [navigation] = performance.getEntriesByType("navigation");
    if (navigation === undefined) return "";
    return new URL(navigation.name).search;
  } catch {
    // Navigation Timing Level 2 missing, or `name` is not an absolute
    // URL. Nothing to recover — fall back to the empty snapshot rather
    // than blocking the JS bundle from initializing.
    return "";
  }
}

/**
 * Snapshot of the query string the user ARRIVED with, parsed at module
 * load time (web only, empty on native). This captures fresh deep
 * links, shared `/squad?tab=…` URLs and hard reloads before — or, via
 * {@link readArrivalSearch}, after — Expo Router's route hydration
 * canonicalizes and strips unrecognized query params via
 * `history.replaceState`.
 * @remarks We capture the entire `?…` bag rather than one named
 * param so any caller can ask via `readInitialRouteParam(key, name)`
 * without each of them duplicating the IIFE.
 */
const INITIAL_URL_PARAMS: Readonly<Record<string, string>> = (() => {
  if (typeof window === "undefined") {
    return Object.freeze({});
  }
  // Prefer the live URL: when this module is evaluated early it is the
  // arrival URL, and it is also the only source that reflects an
  // in-session client-side navigation. Fall back to the navigation
  // entry only once canonicalization has emptied it.
  const search = window.location?.search || readArrivalSearch();
  if (!search) {
    return Object.freeze({});
  }
  try {
    const entries = Array.from(new URLSearchParams(search).entries());
    return Object.freeze(Object.fromEntries(entries));
  } catch {
    // URLSearchParams should not throw on a real browser, but if some
    // exotic environment gives us a malformed `search`, bail out
    // rather than blocking the JS bundle from initializing.
    return Object.freeze({});
  }
})();

/**
 * Compose the storage key for a `(route, param)` pair so two routes
 * with the same param name don't collide.
 * @param routeKey - Stable identifier for the route
 * @param paramName - Param key
 * @returns Fully-qualified sessionStorage key
 */
function storageKey(routeKey: string, paramName: string): string {
  return `${STORAGE_PREFIX}:${routeKey}:${paramName}`;
}

/**
 * Persist a route's param value for the current browser tab.
 *
 * Pass `undefined` to clear the stored value (eg. when the param is
 * intentionally being removed from the URL).
 * @param routeKey - Stable identifier for the route (eg. `"squad"`).
 * Used as a namespace so two routes with the same param name don't
 * collide in storage.
 * @param paramName - Param key (eg. `"tab"`)
 * @param value - The value to remember, or `undefined` to forget
 */
export function persistRouteParam(
  routeKey: string,
  paramName: string,
  value: string | undefined
): void {
  if (!isAvailable()) return;
  const key = storageKey(routeKey, paramName);
  try {
    if (value === undefined) {
      window.sessionStorage.removeItem(key);
      return;
    }
    window.sessionStorage.setItem(key, value);
  } catch {
    // Best-effort mirror: swallow storage failures (SecurityError, QuotaExceededError).
  }
}

/**
 * Read a previously-persisted route param.
 *
 * Returns `undefined` when nothing is stored or when sessionStorage
 * is unavailable (native, SSR). Callers should treat the return
 * value as a fallback — the live URL param remains the source of
 * truth and should be preferred when present.
 * @param routeKey - Stable identifier for the route (eg. `"squad"`)
 * @param paramName - Param key (eg. `"tab"`)
 * @returns The stored value, or `undefined` if absent
 */
export function readPersistedRouteParam(
  routeKey: string,
  paramName: string
): string | undefined {
  if (!isAvailable()) return undefined;
  try {
    const value = window.sessionStorage.getItem(
      storageKey(routeKey, paramName)
    );
    return value ?? undefined;
  } catch {
    return undefined;
  }
}

/**
 * Read the param a screen should seed from on mount.
 *
 * Two layers, tried in order:
 *
 *   1. The module-load URL snapshot. Captures fresh deep links and
 *      hard-reload arrivals before Expo Router's canonicalization
 *      has had a chance to strip the param via `history.replaceState`.
 *      The value is unscoped to a route — anyone navigating directly
 *      to `/foo?bar=baz` is asking for `bar=baz` to be applied.
 *
 *   2. The sessionStorage mirror, namespaced by `(routeKey, paramName)`.
 *      Covers in-session reloads where the user previously changed the
 *      param via the UI (each setter mirrors to storage), so the URL
 *      no longer has it but the user's intent is still recoverable.
 *
 * On native both layers are no-ops and this returns `undefined`;
 * React Navigation handles state persistence its own way.
 * @param routeKey - Stable identifier for the route (eg. `"squad"`)
 * @param paramName - Param key (eg. `"tab"`)
 * @returns The seed value, or `undefined` if neither layer has one
 */
export function readInitialRouteParam(
  routeKey: string,
  paramName: string
): string | undefined {
  const fromUrl = INITIAL_URL_PARAMS[paramName];
  if (fromUrl !== undefined) return fromUrl;
  return readPersistedRouteParam(routeKey, paramName);
}
